import React from 'react'
// libs
import { useLazyQuery } from '@apollo/client/react'
import { WatchQueryFetchPolicy } from '@apollo/client'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// graphql
import { DEFAULT_QUERY } from 'graphql/default'
// type
import { IField } from 'types/graphql'
import { GroupBase } from 'react-select'
import { Validators } from 'utils/validators'
import { DocumentNode } from '@apollo/client/core'
import { WrappedFieldInputProps } from 'redux-form/lib/Field'
import { CustomSelectProps, IOption } from 'components/FormElements/Select'

export interface MultiValueLabelProps {
  field?: IField
  canSetPermissions?: boolean
}

interface Props extends CustomSelectProps, MultiValueLabelProps {
  skip?: boolean
  query: DocumentNode
  planId?: string
  source?: string[]
  validate?: Validators
  variables?: Record<string, any>
  getOptions: (data?: any) => any
  fetchPolicy?: WatchQueryFetchPolicy
  handleSearch?: (data?: any) => any
  prepareOptions?: (data?: any) => any
  onSelectChange?: (value: IOption, input: WrappedFieldInputProps) => void
}

const ReduxEntitySelect: React.FC<Props> = ({
  skip,
  query,
  planId,
  source,
  variables,
  getOptions,
  fetchPolicy = 'network-only',
  handleSearch,
  prepareOptions,
  ...rest
}) => {
  const [options, setOptions] = React.useState<IOption[] | GroupBase<IOption>[]>([])

  const firstFetchRef = React.useRef(false)

  const [fetchOptions, { loading, fetchMore, variables: queryVariables }] = useLazyQuery(query || DEFAULT_QUERY, {
    fetchPolicy
  })

  const getOptionsFromData = (data: Record<string, any>) => {
    const options = getOptions({ data, planId }) || []

    return prepareOptions ? prepareOptions(options) : options
  }

  const onSearch = async (variables: Record<string, any>) => {
    const { data } = await fetchMore({ variables })

    setOptions(getOptionsFromData(data))
  }

  const onInputChange = (search: string) =>
    handleSearch?.({
      variables: queryVariables,
      search,
      refetch: onSearch
    })

  const onMenuOpen = async () => {
    if (!firstFetchRef.current && query) {
      const { data } = await fetchOptions({
        variables
      })

      setOptions(getOptionsFromData(data))

      firstFetchRef.current = true
    }
  }

  React.useEffect(() => {
    if (source) {
      firstFetchRef.current = false
    }
  }, [source, rest.field?.limitResponse, rest.field?.displayEmail])

  return (
    <ReduxSelect options={options} isLoading={loading} handleSearch={onInputChange} onMenuOpen={onMenuOpen} {...rest} />
  )
}

export default ReduxEntitySelect
